<template>
    <div
        :class="['image-responsive', classes, { loading: status === 'loading' }]"
        ref="wrapper"
        :style="{ backgroundColor }"
    >
        <img
            :alt="alt"
            class="image-responsive-img"
            ref="image"
            :sizes="computedSizes"
            :src="placeholder"
            :style="{ width: `${width}px`, height: `${height}px` }"
            v-if="!background"
        >
    </div>
</template>

<script>
/**
 * Apply data object values to given template.
 * @example
 * replaceString('?w=:width&h=:height', {width: 250, height: 125}); // return "?w=250&h=125"
 * @param {String} template
 * @param {Object} data
 * @return {String}
 */
function replaceString(template, data) {
    if (!data) {
        return template;
    }

    return Object.entries(data)
        .reduce((acc, [key, value]) => {
            value = String(value);
            const placeholder = key.toLowerCase();
            return acc.replace(`:${placeholder}`, value);
        }, template);
}

export default {
    props: {
        alt: {
            default: '',
            type: String,
        },
        background: {
            default: false,
            type: Boolean,
        },
        backgroundColor: {
            default: 'transparent',
            type: String,
        },
        cdnUrl: {
            default: '',
            type: String,
        },
        classes: {
            default: '',
            type: String,
        },
        defaultSize: {
            default: 1080,
            type: Number,
        },
        height: {
            default: null,
            type: Number,
        },
        lazyLoad: {
            default: true,
            type: Boolean,
        },
        placeholder: {
            default: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
            type: String,
        },
        sizes: {
            default() {
                return [];
            },
            type: Array,
        },
        src: {
            required: true,
            type: String,
        },
        srcset: {
            default() {
                return [320, 480, 800];
            },
            type: Array,
        },
        width: {
            default: null,
            type: Number,
        },
    },

    data() {
        return {
            status: 'loading',
            unobserve: null,
        };
    },

    computed: {
        computedSizes() {
            let sizesString = '';

            if (this.sizes && this.sizes.length && (this.sizes.length === this.srcset.length)) {
                sizesString += this.srcset.map((size, idx) => `(max-width: ${size}px) ${this.sizes[idx]}px`).join(', ');
            } else {
                sizesString += this.srcset.map((size) => `(max-width: ${size}px) ${size}px`).join(', ');
            }

            sizesString += ` ${this.defaultSize}px`;

            return sizesString;
        },

        computedSrcset() {
            if (this.srcset) {
                return this.srcset
                    .map((size) => replaceString(`${this.cdnUrl}${this.src}`, {width: size}))
                    .join(', ');
            }

            return [];
        },
    },

    methods: {
        preloadImage(source, timeout = 0) {
            return new Promise((resolve) => {
                const image = new Image();

                image.onerror = () => {
                    resolve(this.placeholder);
                };

                image.onload = function () {
                    resolve(source);
                };

                setTimeout(() => {
                    image.src = source;
                }, timeout);
            });
        },

        async setImage(element) {
            const source = await this.preloadImage(this.src);

            if (this.background) {
                element.style.backgroundImage = `url(${source})`;
            } else {
                element.srcset = this.computedSrcset;
                element.src = source;
            }
        },

        async setup() {
            if (this.lazyLoad) {
                this.unobserve = this.watchElementVisibility(this.$el, async (isVisible) => {
                    if (isVisible) {
                        let element = this.$refs.wrapper;

                        if (!this.background) {
                            element = this.$refs.image;

                            element.onload = () => {
                                element.onload = null;
                                this.status = 'ready';
                            };
                        }

                        await this.setImage(element);
                        this.unobserve();
                    }
                });
                this.$once('hook:beforeDestroy', () => {
                    this.unobserve();
                });
            } else {
                const img = this.$refs.image;
                await this.setImage(img);
                this.status = 'ready';
            }
        },

        watchElementVisibility(element, callback) {
            if (typeof window === 'object' && 'IntersectionObserver' in window) {
                // @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Intersection_observer_concepts_and_usage
                const observer = new IntersectionObserver((entries, observer) => {
                    entries.forEach((entry) => {
                        if (entry.target === element) {
                            callback(entry.isIntersecting, observer);
                        }
                    });
                }, {
                    threshold: [0, 0.01],
                });

                observer.observe(element);

                return () => {
                    observer.unobserve(element);
                    observer.disconnect();
                };
            } else {
                callback(true, null);
                return () => null;
            }
        },
    },

    mounted() {
        this.setup();
    },
};
</script>

<style lang="scss">
.image-responsive {
    display: inline-block;
    position: relative;

    &.loading {
        .image-responsive-img {
            opacity: 0;
        }
    }

    .image-responsive-img {
        opacity: 1;
        transition: opacity 300ms ease;
    }
}
</style>
